import React, { useState, useEffect } from 'react';
import { Snackbar, Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Tooltip,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment, } from '@mui/material';
import { Save as SaveIcon, Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { jobGroupsAPI, JobGroup, organizationalUnitsAPI, OrganizationalUnit, companiesAPI, Company, systemSettingsAPI, getImageUrl } from '../../services/api';
import { toPersianNumber, toEnglishNumber } from '../../utils/persianNumber';
import AdminManagers from './Managers';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const JobGroupsTab: React.FC = () => {
  const [groups, setGroups] = useState<JobGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<JobGroup | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    reservationLimit: 1,
    cancellationLimit: 1,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);

  const fetchGroups = async () => {
    try {
      const data = await jobGroupsAPI.getJobGroups();
      setGroups(data);
    } catch (err) {
      setError('خطا در دریافت لیست گروه‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleOpen = (group?: JobGroup) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        title: group.title,
        reservationLimit: group.reservationLimit,
        cancellationLimit: group.cancellationLimit || 0,
      });
    } else {
      setEditingGroup(null);
      setFormData({ title: '', reservationLimit: 1, cancellationLimit: 1 });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingGroup(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingGroup) {
        await jobGroupsAPI.updateJobGroup(editingGroup.id, formData);
      } else {
        await jobGroupsAPI.createJobGroup(formData);
      }
      fetchGroups();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ذخیره اطلاعات');
    }
  };

  const handleDeleteClick = (id: string) => {
    setGroupToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (groupToDelete) {
      try {
        await jobGroupsAPI.deleteJobGroup(groupToDelete);
        fetchGroups();
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'خطا در حذف گروه');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif' }}>
          مدیریت گروه‌های شغلی
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ fontFamily: 'Vazir, sans-serif' }}>
          افزودن گروه جدید
        </Button>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>ردیف</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>عنوان شغلی</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>محدودیت رزرو (روز)</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>محدودیت کنسلی (روز)</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group, index) => (
              <TableRow key={group.id}>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber((index + 1).toString())}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{group.title}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{group.reservationLimit} روز از حالا</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{group.cancellationLimit || 0} روز قبل از وعده</TableCell>
                <TableCell>
                  <Tooltip title="ویرایش">
                    <IconButton onClick={() => handleOpen(group)} size="small" color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="حذف">
                    <IconButton onClick={() => handleDeleteClick(group.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>
          {editingGroup ? 'ویرایش گروه شغلی' : 'افزودن گروه شغلی'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="عنوان شغلی"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              label="محدودیت رزرو (تعداد روز از امروز)"
              value={formData.reservationLimit}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value);
                if (val === '' || !isNaN(Number(val))) setFormData({ ...formData, reservationLimit: val === '' ? 0 : parseInt(val) })
              }}
              helperText="مثال: ۰ یعنی از امروز به بعد، ۳ یعنی از ۳ روز آینده به بعد"
              fullWidth
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif' }, inputMode: 'numeric' }}
              FormHelperTextProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              label="محدودیت کنسلی (تعداد روز قبل از وعده)"
              value={formData.cancellationLimit}
              onChange={(e) => {
                const val = toEnglishNumber(e.target.value);
                if (val === '' || !isNaN(Number(val))) setFormData({ ...formData, cancellationLimit: val === '' ? 0 : parseInt(val) })
              }}
              helperText="مثال: ۱ یعنی تا ۱ روز قبل امکان کنسلی وجود دارد"
              fullWidth
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif' }, inputMode: 'numeric' }}
              FormHelperTextProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ fontFamily: 'Vazir, sans-serif' }}>انصراف</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>حذف گروه شغلی</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
            آیا از حذف این گروه شغلی اطمینان دارید؟ این عملیات غیرقابل برگشت است.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
            انصراف
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const OrganizationalUnitsTab: React.FC = () => {
  const [units, setUnits] = useState<OrganizationalUnit[]>([]);
  // a unit title is only unique inside one company: "انبار" exists under several companies
  const [unitCompanies, setUnitCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<OrganizationalUnit | null>(null);
  const [formData, setFormData] = useState<{ title: string; companyId: string }>({
    title: '',
    companyId: '',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<string | null>(null);

  const fetchUnits = async () => {
    try {
      const [data, companyList] = await Promise.all([
        organizationalUnitsAPI.getOrganizationalUnits(),
        companiesAPI.getCompanies().catch(() => []),
      ]);
      setUnits(data);
      setUnitCompanies(companyList);
    } catch (err) {
      setError('خطا در دریافت لیست واحدهای سازمانی');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  const handleOpen = (unit?: OrganizationalUnit) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        title: unit.title,
        companyId: unit.companyId || '',
      });
    } else {
      setEditingUnit(null);
      setFormData({ title: '', companyId: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUnit(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingUnit) {
        await organizationalUnitsAPI.updateOrganizationalUnit(editingUnit.id, formData);
      } else {
        await organizationalUnitsAPI.createOrganizationalUnit(formData);
      }
      fetchUnits();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ذخیره اطلاعات');
    }
  };

  const handleDeleteClick = (id: string) => {
    setUnitToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (unitToDelete) {
      try {
        await organizationalUnitsAPI.deleteOrganizationalUnit(unitToDelete);
        fetchUnits();
        setDeleteDialogOpen(false);
        setUnitToDelete(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'خطا در حذف واحد سازمانی');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif' }}>
          مدیریت واحدهای سازمانی
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ fontFamily: 'Vazir, sans-serif' }}>
          افزودن واحد جدید
        </Button>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>ردیف</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>عنوان واحد</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>شرکت</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>منبع</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {units.map((unit, index) => (
              <TableRow key={unit.id}>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber((index + 1).toString())}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{unit.title}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', color: unit.companyTitle ? 'text.primary' : 'text.disabled' }}>
                  {unit.companyTitle || 'بدون شرکت'}
                </TableCell>
                <TableCell>
                  {unit.externalcode
                    ? <Chip size="small" label={`همگام‌سازی · کد ${toPersianNumber(unit.externalcode)}`} sx={{ fontFamily: 'Vazir, sans-serif' }} />
                    : <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: 'text.secondary' }}>دستی</Typography>}
                </TableCell>
                <TableCell>
                  <Tooltip title="ویرایش">
                    <IconButton onClick={() => handleOpen(unit)} size="small" color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="حذف">
                    <IconButton onClick={() => handleDeleteClick(unit.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>
          {editingUnit ? 'ویرایش واحد سازمانی' : 'افزودن واحد سازمانی'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="عنوان واحد"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            />
            <TextField
              select
              label="شرکت"
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              helperText="یک عنوان می‌تواند در چند شرکت تکرار شود"
              fullWidth
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              FormHelperTextProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            >
              <MenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>بدون شرکت</MenuItem>
              {unitCompanies.map((company) => (
                <MenuItem key={company.id} value={company.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>{company.title}</MenuItem>
              ))}
            </TextField>
            {editingUnit?.externalcode && (
              <Alert severity="info" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                این واحد از دیتابیس حضور و غیاب همگام‌سازی می‌شود؛ عنوان آن در همگام‌سازی بعدی با نام دپارتمان مبدأ بازنویسی می‌شود.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ fontFamily: 'Vazir, sans-serif' }}>انصراف</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>حذف واحد سازمانی</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
            آیا از حذف این واحد سازمانی اطمینان دارید؟ این عملیات غیرقابل برگشت است.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
            انصراف
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const CompaniesTab: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    title: '',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<string | null>(null);

  const fetchCompanies = async () => {
    try {
      const data = await companiesAPI.getCompanies();
      setCompanies(data);
    } catch (err) {
      setError('خطا در دریافت لیست شرکت‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleOpen = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        title: company.title,
      });
    } else {
      setEditingCompany(null);
      setFormData({ title: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCompany(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingCompany) {
        await companiesAPI.updateCompany(editingCompany.id, formData);
      } else {
        await companiesAPI.createCompany(formData);
      }
      fetchCompanies();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ذخیره اطلاعات');
    }
  };

  const handleDeleteClick = (id: string) => {
    setCompanyToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (companyToDelete) {
      try {
        await companiesAPI.deleteCompany(companyToDelete);
        fetchCompanies();
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
      } catch (err: any) {
        setError(err.response?.data?.error || 'خطا در حذف شرکت');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif' }}>
          مدیریت شرکت‌های زیرمجموعه
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ fontFamily: 'Vazir, sans-serif' }}>
          افزودن شرکت جدید
        </Button>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>ردیف</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>نام شرکت</TableCell>
              <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.map((company, index) => (
              <TableRow key={company.id}>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber((index + 1).toString())}</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{company.title}</TableCell>
                <TableCell>
                  <Tooltip title="ویرایش">
                    <IconButton onClick={() => handleOpen(company)} size="small" color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="حذف">
                    <IconButton onClick={() => handleDeleteClick(company.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>
          {editingCompany ? 'ویرایش شرکت' : 'افزودن شرکت'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="نام شرکت"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ fontFamily: 'Vazir, sans-serif' }}>انصراف</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif' }}>حذف شرکت</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>
            آیا از حذف این شرکت اطمینان دارید؟ اگر پرسنلی به این شرکت متصل باشند امکان حذف وجود ندارد.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
            انصراف
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ fontFamily: 'Vazir, sans-serif' }}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const SyncSettingsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSqlPassword, setShowSqlPassword] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);

  const defaultFieldMap = {
    personnelCode: '',
    firstName: '',
    lastName: '',
    mobile: '',
    jobTitle: 'Prs_Job',
    gender: 'Prs_Sex',
    company: 'Prs_Company',
    cardNumber: '',
    isActive: '',
    unitCode: '',
    unitTitle: ''
  };

  const [formData, setFormData] = useState<import('../../services/api').SyncConfig>({
    host: '',
    port: 1433,
    username: '',
    password: '',
    database: '',
    tableName: '',
    dbType: 'mssql',
    fieldMap: defaultFieldMap,
    cronEnabled: false,
  });

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await import('../../services/api').then(m => m.syncAPI.getConfig());
      if (typeof data.fieldMap === 'string') {
        try {
          data.fieldMap = JSON.parse(data.fieldMap);
        } catch (e) {
          data.fieldMap = defaultFieldMap;
        }
      } else if (!data.fieldMap) {
        data.fieldMap = defaultFieldMap;
      }
      setFormData(data);
    } catch (err) {
      console.log('No existing sync config or error fetching:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleFieldMapChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      fieldMap: {
        ...(prev.fieldMap || defaultFieldMap),
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await import('../../services/api').then(m => m.syncAPI.updateConfig(formData));
      setSuccess('تنظیمات اتصال با موفقیت ذخیره شد.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  const handleManualSync = async () => {
    if (!window.confirm('آیا از اجرای همگام‌سازی اطمینان دارید؟ در صورت بالا بودن حجم دیتای مبدا ممکن است چند دقیقه طول می‌کشد.')) return;
    try {
      setSyncing(true);
      setError('');
      setSyncResult(null);
      const data = await import('../../services/api').then(m => m.syncAPI.executeManualSync());
      setSyncResult(data);
      setSuccess(data.message || 'همگام‌سازی انجام شد.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در اجرای همگام‌سازی');
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckConnection = async () => {
    try {
      setCheckingConnection(true);
      setError('');
      setSuccess('');
      const data = await import('../../services/api').then(m => m.syncAPI.checkConnection(formData));
      if (data.success) {
        setSuccess(data.message || 'اتصال به دیتابیس با موفقیت برقرار شد.');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.error || 'خطا در ارتباط با دیتابیس');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در ارتباط با دیتابیس');
    } finally {
      setCheckingConnection(false);
    }
  };

  if (loading) return <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>در حال بارگذاری...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif' }}>
          تنظیمات اتصال به دیتابیس حضور و غیاب (SQL Server)
        </Typography>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 3 }}>
        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>
              اطلاعات سرور پایگاه داده
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="db-type-label" sx={{ fontFamily: 'Vazir, sans-serif' }}>نوع پایگاه داده</InputLabel>
              <Select
                labelId="db-type-label"
                value={formData.dbType || 'mssql'}
                label="نوع پایگاه داده"
                onChange={(e) => setFormData({ ...formData, dbType: e.target.value })}
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="mssql" sx={{ fontFamily: 'Vazir, sans-serif' }}>Microsoft SQL Server (معمولاً برای PWK, GTS, دنیای پردازش)</MenuItem>
                <MenuItem value="mysql" sx={{ fontFamily: 'Vazir, sans-serif' }}>MySQL / MariaDB</MenuItem>
                <MenuItem value="postgres" sx={{ fontFamily: 'Vazir, sans-serif' }}>PostgreSQL</MenuItem>
              </Select>
            </FormControl>

            <TextField label="آدرس سرور (IP یا Host)" value={formData.host || ''} onChange={(e) => setFormData({ ...formData, host: e.target.value })} fullWidth InputProps={{ style: { fontFamily: 'Vazir, sans-serif', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
            <TextField label="پورت (پیش‌فرض mssql: 1433, mysql: 3306, pg: 5432)" value={formData.port || ''} onChange={(e) => { const val = toEnglishNumber(e.target.value); setFormData({ ...formData, port: val === '' ? undefined : parseInt(val) }) }} fullWidth InputProps={{ style: { fontFamily: 'Vazir, sans-serif', direction: 'ltr' }, inputMode: 'numeric' }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
            <TextField label="نام دیتابیس (Database Name)" value={formData.database || ''} onChange={(e) => setFormData({ ...formData, database: e.target.value })} fullWidth InputProps={{ style: { fontFamily: 'Vazir, sans-serif', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
            <TextField label="نام کاربری (SQL Username)" value={formData.username || ''} onChange={(e) => setFormData({ ...formData, username: e.target.value })} fullWidth InputProps={{ style: { fontFamily: 'Vazir, sans-serif', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
            <TextField
              label="رمز عبور (SQL Password)"
              type={showSqlPassword ? 'text' : 'password'}
              placeholder="********"
              value={formData.password === '********' ? '' : formData.password || ''}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              autoComplete="new-password"
              helperText={formData.password === '********' ? 'رمز عبور قبلاً تنظیم شده است. برای تغییر، رمز جدید را وارد کنید.' : ''}
              fullWidth
              FormHelperTextProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
              InputProps={{
                style: { fontFamily: 'Vazir, sans-serif', direction: 'ltr' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="نمایش رمز عبور"
                      onClick={() => setShowSqlPassword(!showSqlPassword)}
                      edge="end"
                      size="small"
                    >
                      {showSqlPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
            />

            <FormControlLabel
              control={<Switch checked={formData.cronEnabled || false} onChange={(e) => setFormData({ ...formData, cronEnabled: e.target.checked })} color="primary" />}
              label={<Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>فعال‌سازی همگام‌سازی خودکار شبانه (ساعت ۲ بامداد)</Typography>}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleCheckConnection}
                disabled={syncing || checkingConnection}
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                {checkingConnection ? 'در حال بررسی اتصال...' : 'بررسی اتصال دیتابیس'}
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                {saving ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>
              معادل‌سازی فیلدهای دیتابیس (Data Mapping)
            </Typography>
            <Alert severity="info" sx={{ fontFamily: 'Vazir, sans-serif' }}>
              نام دقیق ستون‌ها (فیلدها) را در دیتابیس هدف (حضور و غیاب) بر اساس نیازمندی سیستم وارد کنید.
            </Alert>

            <TextField
              label="نام جدول پرسنل (Table / View Name)"
              required
              value={formData.tableName || ''}
              onChange={(e) => setFormData({ ...formData, tableName: e.target.value })}
              fullWidth
              InputProps={{ style: { fontFamily: 'Vazir, sans-serif', direction: 'ltr' } }}
              InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif', color: 'primary.main', fontWeight: 'bold' } }}
              sx={{ mb: 2 }}
            />

            <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: 'error.main', fontWeight: 'bold' }}>فیلدهای اجباری</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField label="ستون کد پرسنلی" required value={formData.fieldMap?.personnelCode || ''} onChange={(e) => handleFieldMapChange('personnelCode', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون نام" required value={formData.fieldMap?.firstName || ''} onChange={(e) => handleFieldMapChange('firstName', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون نام خانوادگی" required value={formData.fieldMap?.lastName || ''} onChange={(e) => handleFieldMapChange('lastName', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون شماره کارت" required value={formData.fieldMap?.cardNumber || ''} onChange={(e) => handleFieldMapChange('cardNumber', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون وضعیت فعالیت (Active)" required value={formData.fieldMap?.isActive || ''} onChange={(e) => handleFieldMapChange('isActive', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون شماره موبایل" required value={formData.fieldMap?.mobile || ''} onChange={(e) => handleFieldMapChange('mobile', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
            </Box>

            <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: 'text.secondary', fontWeight: 'bold' }}>فیلدهای اختیاری (در صورت خالی بودن نادیده گرفته می‌شوند)</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField label="ستون عنوان شغلی" value={formData.fieldMap?.jobTitle || ''} onChange={(e) => handleFieldMapChange('jobTitle', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون جنسیت" value={formData.fieldMap?.gender || ''} onChange={(e) => handleFieldMapChange('gender', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون شرکت" value={formData.fieldMap?.company || ''} onChange={(e) => handleFieldMapChange('company', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون کد واحد سازمانی" helperText="کد دپارتمان در دیتابیس حضور و غیاب" value={formData.fieldMap?.unitCode || ''} onChange={(e) => handleFieldMapChange('unitCode', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} FormHelperTextProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
              <TextField label="ستون نام واحد سازمانی" helperText="نام دپارتمان؛ واحدها با کد بالا شناسایی می‌شوند" value={formData.fieldMap?.unitTitle || ''} onChange={(e) => handleFieldMapChange('unitTitle', e.target.value)} InputProps={{ style: { fontFamily: 'monospace', direction: 'ltr' } }} InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} FormHelperTextProps={{ style: { fontFamily: 'Vazir, sans-serif' } }} />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir, sans-serif', mb: 2, fontWeight: 'bold' }}>
                اجرای همگام‌سازی دستی
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', mb: 2, color: 'text.secondary' }}>
                با کلیک روی دکمه زیر، سرور همین الان پرسنل را آپدیت می‌کند.
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleManualSync}
                disabled={syncing || checkingConnection}
                sx={{ fontFamily: 'Vazir, sans-serif', width: '100%', mb: 2 }}
              >
                {syncing ? 'در حال همگام‌سازی متصل به دیتابیس...' : 'همگام‌سازی پرسنل اکنون'}
              </Button>
              {syncResult && syncResult.stats && (
                <Alert severity="success" sx={{ mt: 2, fontFamily: 'Vazir, sans-serif' }}>
                  افزوده شده به رستوران: {syncResult.stats.added} نفر <br />
                  ویرایش/آپدیت شده: {syncResult.stats.updated} نفر <br />
                  کل دیتای خوانده شده: {syncResult.stats.totalScanned} رکورد
                </Alert>
              )}
            </Box>

          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

const AdminSettings: React.FC = () => {
  const { user } = useAuth();
  const hasManagersPermission = user?.role === 'admin' || user?.role === 'superadmin' || user?.permissions?.includes('managers');

  const [tabValue, setTabValue] = useState(0);
  const [systemEnabled, setSystemEnabled] = useState(true);
  const [restaurantName, setRestaurantName] = useState('فودفلو');
  const [logoUrl, setLogoUrl] = useState('');
  const [freeFoodTimeLimitOverride, setFreeFoodTimeLimitOverride] = useState(false);
  const [breakfastStartTime, setBreakfastStartTime] = useState('09:00');
  const [lunchStartTime, setLunchStartTime] = useState('13:00');
  const [dinnerStartTime, setDinnerStartTime] = useState('18:00');
  const [excelCurrency, setExcelCurrency] = useState<'toman' | 'rial'>('toman');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await systemSettingsAPI.getSystemSettings();
        setSystemEnabled(data.systemEnabled !== false);
        if (data.restaurantName) setRestaurantName(data.restaurantName);
        if (data.logoUrl) setLogoUrl(data.logoUrl);
        setFreeFoodTimeLimitOverride(data.freeFoodTimeLimitOverride === true);
        if (data.breakfastStartTime) setBreakfastStartTime(data.breakfastStartTime);
        if (data.lunchStartTime) setLunchStartTime(data.lunchStartTime);
        if (data.dinnerStartTime) setDinnerStartTime(data.dinnerStartTime);
        if (data.excelCurrency) setExcelCurrency(data.excelCurrency as 'toman' | 'rial');
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      try {
        setUploadingLogo(true);
        const url = await systemSettingsAPI.uploadLogo(e.target.files[0]);
        setLogoUrl(url);
        setSuccess('لوگو با موفقیت آپلود شد. لطفا برای اعمال نهایی دکمه ذخیره تنظیمات را بزنید.');
        setTimeout(() => setSuccess(''), 4000);
      } catch (err) {
        console.error('Error uploading logo:', err);
      } finally {
        setUploadingLogo(false);
      }
    }
  };

  const handleSave = async () => {
    try {
      await systemSettingsAPI.updateSystemSettings({
        systemEnabled,
        appEnabled: true,
        restaurantName,
        logoUrl,
        freeFoodTimeLimitOverride,
        breakfastStartTime,
        lunchStartTime,
        dinnerStartTime,
        excelCurrency
      });
      
      const event = new CustomEvent('settingsUpdated', { 
        detail: { restaurantName, logoUrl } 
      });
      window.dispatchEvent(event);

      setSuccess('تنظیمات با موفقیت ذخیره و اعمال شد.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'Vazir, sans-serif',
          fontWeight: 600,
          mb: 4,
        }}
      >
        تنظیمات سیستم
      </Typography>

      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="تنظیمات عمومی" sx={{ fontFamily: 'Vazir, sans-serif' }} />
          <Tab label="گروه‌های شغلی" sx={{ fontFamily: 'Vazir, sans-serif' }} />
          <Tab label="واحدهای سازمانی" sx={{ fontFamily: 'Vazir, sans-serif' }} />
          <Tab label="شرکت‌های زیرمجموعه" sx={{ fontFamily: 'Vazir, sans-serif' }} />
          <Tab label="همگام‌سازی پرسنل" sx={{ fontFamily: 'Vazir, sans-serif' }} />
          <Tab label="تنظیمات گزارشات" sx={{ fontFamily: 'Vazir, sans-serif' }} />
          {hasManagersPermission && <Tab label="کاربران سامانه" sx={{ fontFamily: 'Vazir, sans-serif' }} />}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            {loading ? (
              <Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>در حال بارگذاری...</Typography>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemEnabled}
                      onChange={(e) => setSystemEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: 'Vazir, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      فعال بودن سیستم
                    </Typography>
                  }
                  sx={{ mb: 3, display: 'block' }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={freeFoodTimeLimitOverride}
                      onChange={(e) => setFreeFoodTimeLimitOverride(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: 'Vazir, sans-serif',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                      }}
                    >
                      لغو محدودیت زمان چند روزه برای رزرو و لغو غذای آزاد (اعمال فقط مهلت‌های روزانه ۹، ۱۱ و ۱۷)
                    </Typography>
                  }
                  sx={{ mb: 3, display: 'block' }}
                />

                <TextField
                  label="نام رستوران / سازمان"
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  fullWidth
                  sx={{ mb: 3 }}
                  InputLabelProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
                  InputProps={{ style: { fontFamily: 'Vazir, sans-serif' } }}
                />

                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontFamily: 'Vazir, sans-serif', mb: 1 }}>لوگوی اختصاصی سامانه</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={uploadingLogo}
                      sx={{ fontFamily: 'Vazir, sans-serif' }}
                    >
                      {uploadingLogo ? 'در حال آپلود...' : 'انتخاب تصویر'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                    </Button>
                    {logoUrl && (
                      <Box
                        component="img"
                        src={getImageUrl(logoUrl)}
                        alt="Logo Preview"
                        sx={{ height: 60, objectFit: 'contain' }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif', mt: 1, display: 'block' }}>
                    پیشنهاد می‌شود ابعاد لوگو با نسبت ۳ به ۲ (مثلاً ۱۸۰ در ۱۲۰ پیکسل) باشد تا از به هم‌ریختگی در ظاهر داشبورد جلوگیری شود.
                  </Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{ fontFamily: 'Vazir, sans-serif' }}
                  >
                    ذخیره تنظیمات
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <JobGroupsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <OrganizationalUnitsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <CompaniesTab />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <SyncSettingsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold', mb: 3 }}>
              تنظیمات فایل‌های خروجی اکسل
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <FormLabel component="legend" sx={{ fontFamily: 'Vazir, sans-serif', mb: 1, color: 'text.primary' }}>
                واحد پولی مبالغ در اکسل
              </FormLabel>
              <RadioGroup
                value={excelCurrency}
                onChange={(e) => setExcelCurrency(e.target.value as 'toman' | 'rial')}
              >
                <FormControlLabel 
                  value="toman" 
                  control={<Radio color="primary" />} 
                  label={<Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>تومان</Typography>} 
                />
                <FormControlLabel 
                  value="rial" 
                  control={<Radio color="primary" />} 
                  label={<Typography sx={{ fontFamily: 'Vazir, sans-serif' }}>ریال (اعداد تومان ضربدر ۱۰ می‌شوند)</Typography>} 
                />
              </RadioGroup>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              ذخیره تنظیمات
            </Button>
          </CardContent>
        </Card>
      </TabPanel>

      {hasManagersPermission && (
        <TabPanel value={tabValue} index={6}>
          <AdminManagers />
        </TabPanel>
      )}
    </Box>
  );
};

export default AdminSettings;
